import { API_BASE_URL } from "../../config/apiConfig";
import { useEffect, useState } from "react";
import {
  useNavigate,
} from "react-router-dom";

import PersonalInformation from "./PersonalInformation";
import ProfessionalPageNav from "../../components/professional/ProfessionalPageNav";
import PageLoader from "../../components/common/PageLoader";

import "../../styles/professional.css";
import "../../styles/pages/professional/EditProfessionalProfile.css";

const API_BASE = API_BASE_URL;

const emptyEmergencyContact = {
  name: "",
  relationship: "",
  phone: "",
};

function EditProfessionalProfile() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    profession: "",
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
    profilePhotoUrl: "",
    emergencyContact: {
      ...emptyEmergencyContact,
    },
    languages: [],
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadingIndicator, setShowLoadingIndicator] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLanguageFormOpen, setIsLanguageFormOpen] =
  useState(false);

const [editingLanguageIndex, setEditingLanguageIndex] =
  useState(null);

const [languageDraft, setLanguageDraft] = useState({
  language: "",
  proficiency: "",
});

  useEffect(() => {
    if (!isLoading) {
      setShowLoadingIndicator(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowLoadingIndicator(true);
    }, 800);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isLoading]);

  useEffect(() => {
    async function loadProfile() {
      const token =
        localStorage.getItem("zethAuthToken");

      if (!token) {
        navigate("/professional/login");
        return;
      }

      try {
        /*
         * Use /api/profile instead of /api/auth/me because
         * /api/profile returns all the new profile fields.
         */
        const response = await fetch(
          `${API_BASE}/profile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (!response.ok) {
          if (
            response.status === 401 ||
            response.status === 403
          ) {
            localStorage.removeItem("zethAuthToken");
            localStorage.removeItem("zethUser");

            navigate("/professional/login");
            return;
          }

          throw new Error(
            data.message || "Unable to load profile."
          );
        }

        const user = data.user || data;

        const loadedEmergencyContact =
          user.emergencyContact ||
          user.emergency_contact ||
          emptyEmergencyContact;

        const loadedLanguages = Array.isArray(
          user.languages
        )
          ? user.languages
          : [];

        setFormData({
          firstName:
            user.firstName ||
            user.first_name ||
            "",
          lastName:
            user.lastName ||
            user.last_name ||
            "",
          email: user.email || "",
          phone: user.phone || "",
          dateOfBirth:
            user.dateOfBirth ||
            user.date_of_birth
              ? String(
                  user.dateOfBirth ||
                    user.date_of_birth
                ).slice(0, 10)
              : "",
          profession: user.profession || "",
          address:
            user.address ||
            user.addressLine ||
            user.address_line ||
            "",
          city: user.city || "",
          province: user.province || "",
          postalCode:
            user.postalCode ||
            user.postal_code ||
            "",
          country: user.country || "Canada",
          profilePhotoUrl:
            user.profilePhotoUrl ||
            user.profile_photo_url ||
            "",
          emergencyContact: {
            name:
              loadedEmergencyContact?.name || "",
            relationship:
              loadedEmergencyContact?.relationship ||
              "",
            phone:
              loadedEmergencyContact?.phone || "",
          },
          languages: loadedLanguages.map(
            (item) => {
              if (typeof item === "string") {
                return {
                  language: item,
                  proficiency: "",
                };
              }

              return {
                language:
                  item?.language || "",
                proficiency:
                  item?.proficiency || "",
              };
            }
          ),
        });
      } catch (loadError) {
        console.error(
          "Load profile error:",
          loadError
        );

        setError(
          loadError.message ||
            "Unable to load profile."
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadProfile();
  }, [navigate]);


  function clearMessages() {
    setMessage("");
    setError("");
  }

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));

    clearMessages();
  }

  function handleEmergencyContactChange(event) {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      emergencyContact: {
        ...currentData.emergencyContact,
        [name]: value,
      },
    }));

    clearMessages();
  }

  function handleAddressChange(addressData) {
    setFormData((currentData) => ({
      ...currentData,
      address:
        addressData?.address ??
        currentData.address,
      city:
        addressData?.city ??
        currentData.city,
      province:
        addressData?.province ??
        currentData.province,
      postalCode:
        addressData?.postalCode ??
        currentData.postalCode,
      country:
        addressData?.country ||
        currentData.country ||
        "Canada",
    }));

    clearMessages();
  }

  function openAddLanguageForm() {
  setEditingLanguageIndex(null);

  setLanguageDraft({
    language: "",
    proficiency: "",
  });

  setIsLanguageFormOpen(true);
  clearMessages();
}

function openEditLanguageForm(index) {
  const selectedLanguage =
    formData.languages[index];

  setEditingLanguageIndex(index);

  setLanguageDraft({
    language:
      selectedLanguage?.language || "",
    proficiency:
      selectedLanguage?.proficiency || "",
  });

  setIsLanguageFormOpen(true);
  clearMessages();
}

function handleLanguageDraftChange(event) {
  const { name, value } = event.target;

  setLanguageDraft((currentDraft) => ({
    ...currentDraft,
    [name]: value,
  }));

  clearMessages();
}

function saveLanguage() {
  if (
    !languageDraft.language ||
    !languageDraft.proficiency
  ) {
    setError(
      "Please select a language and proficiency."
    );
    return;
  }

  setFormData((currentData) => {
    const updatedLanguages = [
      ...currentData.languages,
    ];

    if (editingLanguageIndex !== null) {
      updatedLanguages[editingLanguageIndex] = {
        ...languageDraft,
      };
    } else {
      updatedLanguages.push({
        ...languageDraft,
      });
    }

    return {
      ...currentData,
      languages: updatedLanguages,
    };
  });

  setLanguageDraft({
    language: "",
    proficiency: "",
  });

  setEditingLanguageIndex(null);
  setIsLanguageFormOpen(false);
  clearMessages();
}

function cancelLanguageForm() {
  setLanguageDraft({
    language: "",
    proficiency: "",
  });

  setEditingLanguageIndex(null);
  setIsLanguageFormOpen(false);
  clearMessages();
}

function removeLanguage(index) {
  setFormData((currentData) => ({
    ...currentData,
    languages: currentData.languages.filter(
      (_, languageIndex) =>
        languageIndex !== index
    ),
  }));

  if (editingLanguageIndex === index) {
    cancelLanguageForm();
  }

  clearMessages();
}

  async function handleSubmit(event) {
    event.preventDefault();

    setError("");
    setMessage("");
    setIsSaving(true);

    const token =
      localStorage.getItem("zethAuthToken");

    if (!token) {
      setIsSaving(false);
      navigate("/professional/login");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/profile`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone,
            dateOfBirth:
              formData.dateOfBirth,
            profession: formData.profession,
            address: formData.address,
            city: formData.city,
            province: formData.province,
            postalCode: formData.postalCode,
            country:
              formData.country || "Canada",
            profilePhotoUrl:
              formData.profilePhotoUrl,
            emergencyContact:
              formData.emergencyContact,
            languages: formData.languages,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (
          response.status === 401 ||
          response.status === 403
        ) {
          localStorage.removeItem("zethAuthToken");
          localStorage.removeItem("zethUser");

          navigate("/professional/login");
          return;
        }

        throw new Error(
          data.message ||
            "Unable to update profile."
        );
      }

      const updatedUser = data.user || {};

      setFormData((currentData) => ({
        ...currentData,

        firstName:
          updatedUser.firstName ||
          updatedUser.first_name ||
          currentData.firstName,

        lastName:
          updatedUser.lastName ||
          updatedUser.last_name ||
          currentData.lastName,

        email:
          updatedUser.email ||
          currentData.email,

        phone:
          updatedUser.phone ||
          currentData.phone,

        dateOfBirth:
          updatedUser.dateOfBirth ||
          updatedUser.date_of_birth
            ? String(
                updatedUser.dateOfBirth ||
                  updatedUser.date_of_birth
              ).slice(0, 10)
            : currentData.dateOfBirth,

        profession:
          updatedUser.profession ||
          currentData.profession,

        address:
          updatedUser.address ??
          updatedUser.address_line ??
          currentData.address,

        city:
          updatedUser.city ??
          currentData.city,

        province:
          updatedUser.province ??
          currentData.province,

        postalCode:
          updatedUser.postalCode ??
          updatedUser.postal_code ??
          currentData.postalCode,

        country:
          updatedUser.country ||
          currentData.country ||
          "Canada",

        profilePhotoUrl:
          updatedUser.profilePhotoUrl ??
          updatedUser.profile_photo_url ??
          currentData.profilePhotoUrl,

        emergencyContact:
          updatedUser.emergencyContact ||
          updatedUser.emergency_contact ||
          currentData.emergencyContact,

        languages: Array.isArray(
          updatedUser.languages
        )
          ? updatedUser.languages
          : currentData.languages,
      }));

      const currentStoredUser = JSON.parse(
        localStorage.getItem("zethUser") ||
          "{}"
      );

      const updatedStoredUser = {
        ...currentStoredUser,
        ...updatedUser,

        firstName:
          updatedUser.firstName ||
          updatedUser.first_name ||
          formData.firstName,

        lastName:
          updatedUser.lastName ||
          updatedUser.last_name ||
          formData.lastName,

        phone:
          updatedUser.phone ||
          formData.phone,

        dateOfBirth:
          updatedUser.dateOfBirth ||
          updatedUser.date_of_birth ||
          formData.dateOfBirth,

        profession:
          updatedUser.profession ||
          formData.profession,

        address:
          updatedUser.address ??
          updatedUser.address_line ??
          formData.address,

        city:
          updatedUser.city ??
          formData.city,

        province:
          updatedUser.province ??
          formData.province,

        postalCode:
          updatedUser.postalCode ??
          updatedUser.postal_code ??
          formData.postalCode,

        country:
          updatedUser.country ||
          formData.country ||
          "Canada",

        profilePhotoUrl:
          updatedUser.profilePhotoUrl ??
          updatedUser.profile_photo_url ??
          formData.profilePhotoUrl,

        emergencyContact:
          updatedUser.emergencyContact ||
          updatedUser.emergency_contact ||
          formData.emergencyContact,

        languages: Array.isArray(
          updatedUser.languages
        )
          ? updatedUser.languages
          : formData.languages,
      };

      localStorage.setItem(
        "zethUser",
        JSON.stringify(updatedStoredUser)
      );

      setMessage(
        data.message ||
          "Profile updated successfully."
      );
    } catch (updateError) {
      console.error(
        "Update profile error:",
        updateError
      );

      setError(
        updateError.message ||
          "Unable to update profile."
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (
    isLoading &&
    showLoadingIndicator
  ) {
    return (
      <PageLoader
        title="Personal Information"
        message="Loading your personal information..."
      />
    );
  }

  if (isLoading) {
    return null;
  }

  return (
    <main className="professional-profile-page">
      <ProfessionalPageNav />

      <div className="profile-page-header">
        <div>
          <p className="account-label">
            Professional Portal
          </p>

          <h1>My Profile</h1>

          <p>
            Review and update your personal
            information.
          </p>
        </div>
      </div>

      <PersonalInformation
        formData={formData}
        handleChange={handleChange}
        handleAddressChange={
          handleAddressChange
        }
        handleEmergencyContactChange={
          handleEmergencyContactChange
        }
        isLanguageFormOpen={isLanguageFormOpen}
editingLanguageIndex={editingLanguageIndex}
languageDraft={languageDraft}
openAddLanguageForm={openAddLanguageForm}
openEditLanguageForm={openEditLanguageForm}
handleLanguageDraftChange={
  handleLanguageDraftChange
}
saveLanguage={saveLanguage}
cancelLanguageForm={cancelLanguageForm}
removeLanguage={removeLanguage}
        handleSubmit={handleSubmit}
        isSaving={isSaving}
        message={message}
        error={error}
      />

    </main>
  );
}

export default EditProfessionalProfile;
